import Button, { ButtonSizes, ButtonStyle } from '@components/Button';
import IconRounded, {
  IconRoundedSizes,
  IconRoundedStyles,
} from '@components/IconRounded';
import {
  type DiscountType,
  type EventType,
  type Ticket,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useCheckDiscountTicket, useCodeTicket } from '@utils/apiHooks';
import {
  checkIfDiscountAppliesMinMaxToSelectedTicketIds,
  checkIfDiscountAppliesToSelectedTicketIds,
} from '@utils/checkIfDiscountAppliesMinMax';
import { extractSearchParams, formatDiscountCode } from '@utils/fn';
import React, {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  CodeReferenceInput,
  DiscountSectionRoot,
  InputWrapper,
  StretchItem,
} from './styles';

import Input from '@components/Input';
import { RowAlignedItems } from '@components/LinearLayout';
import { H6 } from '@components/Typography';
import { useHookParams } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

interface DiscountSectionProps {
  discount: DiscountType | undefined;
  eventDetails: EventType;
  setDiscount: Dispatch<SetStateAction<DiscountType | undefined>>;
  setError: Dispatch<SetStateAction<string>>;
  selectedTicketIdsWithOptions: string[];
  invisible?: boolean;
}

const DiscountSection = ({
  discount,
  eventDetails,
  setDiscount,
  setError,
  selectedTicketIdsWithOptions,
  invisible = false,
}: DiscountSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, ticketCode, discountCodeParam] = useHookParams([
    'la',
    'tc',
    'di',
  ]);
  const [showSuccessDiscount, setShowSuccessDiscount] = useState<boolean>(
    Boolean(discount)
  );

  const [discountCode, setDiscountCode] = useState(
    formatDiscountCode(discountCodeParam)
  );

  // Resolve ticket code to ticket object
  const { data: ticketByCodeData } = useCodeTicket({
    code: ticketCode,
  });
  const resolvedTicket = ticketByCodeData?.ticket as Ticket | undefined;
  const resolvedTicketId = resolvedTicket?.id;

  const selectedTicketsIds = useMemo(() => {
    return [...new Set(selectedTicketIdsWithOptions)];
  }, [selectedTicketIdsWithOptions]);

  const shouldApplyDiscountForParamTicket = useMemo(() => {
    return (
      !!discountCodeParam && (!!ticketCode || selectedTicketsIds?.length > 0)
    );
  }, [discountCodeParam, selectedTicketsIds?.length, ticketCode]);

  const [isDiscountApplied, setIsDiscountApplied] = useState<boolean>(
    shouldApplyDiscountForParamTicket
  );

  const isSelectedTicketsDiscountBetweenMinMax =
    checkIfDiscountAppliesMinMaxToSelectedTicketIds(
      selectedTicketsIds?.length > 0
        ? selectedTicketsIds
        : resolvedTicketId
          ? [resolvedTicketId]
          : [],
      discount
    );
  const isSelectedTicketsDiscounted = checkIfDiscountAppliesToSelectedTicketIds(
    selectedTicketsIds?.length > 0
      ? selectedTicketsIds
      : resolvedTicketId
        ? [resolvedTicketId]
        : [],
    discount
  );

  // Use resolved ticket ID as fallback when no tickets are selected
  const ticketIdsForApi =
    selectedTicketsIds.length > 0
      ? selectedTicketsIds
      : resolvedTicketId
        ? [resolvedTicketId]
        : [];
  const apiCallEnabled =
    isDiscountApplied &&
    Boolean(eventDetails?.id?.length) &&
    ticketIdsForApi.length > 0;
  const {
    data,
    isFetched,
    error: discountError,
  } = useCheckDiscountTicket(
    selectedTicketsIds,
    discountCode,
    language,
    eventDetails?.id,
    apiCallEnabled
  );

  const discountData = data?.discount?.result?.data;

  const handleApplyDiscount = useCallback(() => {
    if (discountCode?.trim().length > 0) {
      setDiscountCode(formatDiscountCode(discountCode));
      setIsDiscountApplied(true);
    }
  }, [discountCode]);

  const removeDiscountCode = useCallback(() => {
    setIsDiscountApplied(false);
    setDiscountCode('');
    setShowSuccessDiscount(false);
    setDiscount(undefined);
  }, [setDiscount]);

  const handleDiscountCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = formatDiscountCode(event.target.value);
      setDiscountCode(newValue);
      setIsDiscountApplied(false);
      setShowSuccessDiscount(false);
      setDiscount(undefined);
    },
    [setDiscount]
  );

  // Handle errors from React Query
  useEffect(() => {
    if (discountError) {
      setError(discountError?.message || 'Failed to load discount');
    } else {
      setError('');
    }
  }, [discountError, setError]);

  // Separate effect to handle discount application when event details become available
  useEffect(() => {
    if (discountCodeParam && eventDetails?.id) {
      handleApplyDiscount();
    }
  }, [eventDetails?.id, discountCodeParam, handleApplyDiscount]);

  useEffect(() => {
    const shouldApply =
      discountCodeParam &&
      (ticketCode || selectedTicketsIds.length > 0) &&
      eventDetails?.id;

    if (shouldApply) {
      handleApplyDiscount();
    }
  }, [
    discountCodeParam,
    ticketCode,
    selectedTicketsIds,
    handleApplyDiscount,
    eventDetails?.id,
  ]);

  useEffect(() => {
    // Only reset discount state if no tickets selected AND no ticketCode parameter
    if (selectedTicketsIds.length === 0 && !ticketCode && !discountCodeParam) {
      setIsDiscountApplied(false);
      setShowSuccessDiscount(false);
      setDiscount(undefined);
    }
  }, [
    setDiscount,
    selectedTicketsIds,
    setIsDiscountApplied,
    setShowSuccessDiscount,
    ticketCode,
    discountCodeParam,
  ]);

  useEffect(() => {
    if (isFetched && data?.discount?.status === 200 && isDiscountApplied) {
      if (
        discountData?.expire_at != null &&
        new Date(discountData.expire_at as string).getTime() <
          new Date().getTime()
      ) {
        setIsDiscountApplied(false);
        setError(t('discount-expired') ?? '');
      } else if (
        discountData?.valid_from != null &&
        new Date(discountData.valid_from as string).getTime() >
          new Date().getTime()
      ) {
        setIsDiscountApplied(false);
        setError(t('discount-not-valid') ?? '');
      } else {
        setDiscount(discountData as DiscountType);
        setShowSuccessDiscount(true);
        if (!discountCodeParam && discountData) {
          setSearchParams({
            ...extractSearchParams(searchParams),
            di: discountCode,
          });
        }
      }
    } else if (!navigator.onLine) {
      setIsDiscountApplied(false);
    }
  }, [
    discountData,
    isDiscountApplied,
    setIsDiscountApplied,
    discountCode,
    searchParams,
    setSearchParams,
    setDiscount,
    setError,
    t,
    discountCodeParam,
    isFetched,
    data?.discount?.status,
  ]);

  useEffect(() => {
    if (selectedTicketIdsWithOptions?.length > 0 && showSuccessDiscount) {
      if (!isSelectedTicketsDiscounted) {
        setError(t('discount-unavailable-selected-tickets') ?? '');
      } else if (
        isSelectedTicketsDiscounted &&
        !isSelectedTicketsDiscountBetweenMinMax
      ) {
        setError(
          t('discount-invalid-min-max', {
            min: discount?.min_ticket,
            max: discount?.max_ticket,
          }) ?? ''
        );
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [
    discount?.max_ticket,
    discount?.min_ticket,
    isSelectedTicketsDiscountBetweenMinMax,
    isSelectedTicketsDiscounted,
    selectedTicketIdsWithOptions?.length,
    setError,
    showSuccessDiscount,
    t,
  ]);

  if (invisible) {
    return null;
  }

  return (
    <DiscountSectionRoot>
      <StretchItem data-testid="discount:discount-text">
        {showSuccessDiscount ? (
          isSelectedTicketsDiscounted &&
          isSelectedTicketsDiscountBetweenMinMax && (
            <H6>{t('discount-added')}</H6>
          )
        ) : (
          <H6>{t('you-have-a-discount-code')}</H6>
        )}
      </StretchItem>
      <StretchItem>
        <CodeReferenceInput data-testid="discount:reference">
          <InputWrapper data-testid="discount:code">
            <Input
              background={
                showSuccessDiscount ? theme.color.gradientGreen : undefined
              }
              backgroundColor={
                !showSuccessDiscount ? theme.color.lightLush : undefined
              }
              iconName={showSuccessDiscount ? 'check' : undefined}
              value={discountCode?.trim().toUpperCase()}
              placeholder={String(t('enter-discount-code'))}
              borderRadius="5rem"
              border={
                showSuccessDiscount
                  ? `0.1rem solid ${theme.color.success2}`
                  : `0.1rem solid ${theme.color.grey1}`
              }
              color={theme.color.greyMediumDark}
              textAlign="center"
              padding="0 1.5rem"
              onChange={handleDiscountCodeChange}
            />
          </InputWrapper>
          {showSuccessDiscount ? (
            <RowAlignedItems data-testid="discount:remove" gap="1rem">
              <IconRounded
                iconStyle={IconRoundedStyles.default}
                iconSize={IconRoundedSizes.small}
                iconName={'close'}
                overrideBackgroundColor={theme.color.error}
                onClick={removeDiscountCode}
              />
            </RowAlignedItems>
          ) : (
            <Button
              data-testid="discount:apply"
              buttonSize={ButtonSizes.medium}
              buttonStyle={ButtonStyle.outline}
              border={`0.1rem solid ${theme.color.grey1}`}
              boxShadow="0 0.2rem 0 rgba(0, 0, 0, 0.1)"
              color={theme.color.greyDark}
              disabled={
                discountCode?.length < 6 ||
                selectedTicketIdsWithOptions.length === 0
              }
              content={t('apply')}
              onClick={handleApplyDiscount}
            />
          )}
        </CodeReferenceInput>
      </StretchItem>
    </DiscountSectionRoot>
  );
};

export default DiscountSection;
